"use client";

import React, { useState, useEffect } from "react";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import ReactPaginate from "react-paginate";
import Loader from "../Loader/Loader";
import { FaArrowDown, FaArrowUp, FaDollarSign, FaEdit } from "react-icons/fa";
import DiscountModal from "../Clients/DiscountModal";
import { Link } from "react-router-dom";
import {
  getClientEmailFromRecord,
  getClientIdFromRecord,
  getClientNameFromRecord,
} from "../../utilis/applicationCostHelpers";

const ProfessionalCost = () => {
  const [selectedApplications, setSelectedApplications] = useState(new Set());
  const [applicationWithPaymentRecord, setApplicationWithPaymentRecord] =
    useState([]);
  const [applicationsStatistics, setApplicationStatistics] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("");
  const [selectedApplication, setSelectedApplication] = useState({});
  const [clientData, setClientData] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [openDiscountModal, setOpenDiscountModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getValue = (item, key) => {
    if (
      key === "total_charges" ||
      key === "total_payments" ||
      key === "total_discounts" ||
      key === "remaining_balance"
    ) {
      return item.financial_summary?.[key] || 0;
    }
    if (key === "status") {
      return item.financial_summary?.status || "";
    }
    return item[key] || "";
  };

  const sortedRecords = React.useMemo(() => {
    let sortableItems = [...applicationWithPaymentRecord];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const aValue = getValue(a, sortConfig.key);
        const bValue = getValue(b, sortConfig.key);

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [applicationWithPaymentRecord, sortConfig]);

  // Filter states
  const [filters, setFilters] = useState({
    searchByName: "",
    type: "",
    currency: "",
    status: "",
    amountMin: "",
    amountMax: "",
    dateFrom: "",
    dateTo: "",
  });
  const [filterKey, setFilterKey] = useState(0);

  // Define the table header
  const tableHeaders = {
    client_name: "Client Name",
    application_name: "Application",
    type: "Application Type",
    total_charges: "Professional Cost",
    total_payments: "Paid",
    total_discounts: "Discount",
    remaining_balance: "Remaining Balance",
    status: "Status",
    // plan_frequency: "Plan Frequency",
    // plan_installments_count: "Installments",
    created_at: "Created at",
    actions: "Actions",
  };

  // fetch the applications with their professional cost record
  const fetchApplicationWithInvoices = async () => {
    setLoading(true);
    const params = {
      per_page: pagination.perPage,
      page: pagination.currentPage,
      searchByName: filters.searchByName || undefined,
      type: filters.type || undefined,
      currency: filters.currency || undefined,
      status: filters.status || undefined,
      amountMin: filters.amountMin || undefined,
      amountMax: filters.amountMax || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
    };
    try {
      const response = await apiUrl.get("/fetchApplicationProfessionalCost", {
        params,
      });
      if (response?.status) {
        setApplicationWithPaymentRecord(response?.data?.data);
        setApplicationStatistics(response?.data?.statistics);
        const dataMeta = response?.data?.meta;
        setPagination((prev) => ({
          ...prev,
          total: dataMeta?.total ?? 0,
          lastPage: dataMeta?.last_page,
          perPage: dataMeta?.per_page,
          currentPage: dataMeta?.current_page,
        }));
      }
    } catch (error) {
      console.log("Failed to fetch the applciation with data", error);
    } finally {
      setLoading(false);
    }
  };
  // apply filter
  const applyFilters = () => {
    setFilterKey((prev) => prev + 1);

    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };
  useEffect(() => {
    if (pagination.currentPage > 0) {
      fetchApplicationWithInvoices();
    }
  }, [pagination.currentPage, pagination.perPage, filterKey]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Handle pagination
  const handlePerPageChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      perPage: parseInt(e.target.value),
      currentPage: 1,
    }));
  };

  const handlePageChange = (selectedItem) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: selectedItem.selected + 1,
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      type: "",
      status: "",
      currency: "",
      dateFrom: "",
      dateTo: "",
      amountMin: "",
      amountMax: "",
    });
    setFilterKey((prev) => prev + 1);

    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };
  const handleOpenDiscountModal = (row) => {
    const client = row?.client || {};
    const application = row?.application || {};
    setType(row?.type === "education" ? "edu" : "mig");
    setClientData({
      id: getClientIdFromRecord(client) || application?.clientid,
      name: getClientNameFromRecord(client) || row?.client_name || "",
      email: getClientEmailFromRecord(client),
    });
    setSelectedApplication({
      ...application,
      id: application.id || row.application_id,
      coursename: application.coursename || row.application_name,
      currency: application.currency || row.currency,
      clientid: application.clientid || getClientIdFromRecord(client),
    });
    setOpenDiscountModal(true);
  };
  return (
    <div
      style={{
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
        padding: "1rem",
      }}
    >
      {/* === APPLICATION & FINANCIAL OVERVIEW === */}

      <div
        className="card mb-4 shadow-lg border-0"
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "20px",
        }}
      >
        <div
          className="card-body p-4"
          style={{
            background: "rgba(255,255,255,0.95)",
            borderRadius: "20px",
            margin: "3px",
          }}
        >
          <div className="text-center mb-4">
            <h4
              className="mb-2 d-flex align-items-center justify-content-center"
              style={{
                fontWeight: 700,
                color: "#1e293b",
                fontSize: "1.5rem",
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <i
                className="fas fa-chart-line me-3"
                style={{
                  color: "#667eea",
                  fontSize: "1.8rem",
                  background: "linear-gradient(45deg, #667eea, #764ba2)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              ></i>
              Application & Financial Dashboard
            </h4>

            {/* === Totals Section === */}
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <div
                  className="p-4 rounded-4 text-center position-relative overflow-hidden"
                  style={{
                    background:
                      "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                    border: "2px solid #e2e8f0",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = "translateY(-5px)";
                    e.target.style.boxShadow = "0 10px 25px rgba(0,0,0,0.15)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      color: "#64748b",
                      marginBottom: "0.5rem",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                    }}
                  >
                    <i
                      className="fas fa-file-alt me-2"
                      style={{ color: "#6366f1", fontSize: "1.2rem" }}
                    ></i>
                    Total Applications
                  </div>
                  <div
                    style={{
                      fontWeight: 800,
                      color: "#1e293b",
                      fontSize: "2.2rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {applicationsStatistics?.totals?.total_applications || 0}
                  </div>
                  <div
                    className="badge"
                    style={{
                      backgroundColor: "#6366f1",
                      color: "white",
                      fontSize: "0.75rem",
                      padding: "0.4rem 0.8rem",
                    }}
                  >
                    All Categories
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div
                  className="p-4 rounded-4 text-center position-relative overflow-hidden"
                  style={{
                    background:
                      "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                    border: "2px solid #10b981",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = "translateY(-5px)";
                    e.target.style.boxShadow =
                      "0 10px 25px rgba(16,185,129,0.25)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      color: "#047857",
                      marginBottom: "0.5rem",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                    }}
                  >
                    <i
                      className="fas fa-graduation-cap me-2"
                      style={{ color: "#10b981", fontSize: "1.2rem" }}
                    ></i>
                    Education
                  </div>
                  <div
                    style={{
                      fontWeight: 800,
                      color: "#047857",
                      fontSize: "2.2rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {applicationsStatistics?.totals?.total_educations || 0}
                  </div>
                  <div
                    className="badge"
                    style={{
                      backgroundColor: "#10b981",
                      color: "white",
                      fontSize: "0.75rem",
                      padding: "0.4rem 0.8rem",
                    }}
                  >
                    Academic Programs
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div
                  className="p-4 rounded-4 text-center position-relative overflow-hidden"
                  style={{
                    background:
                      "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                    border: "2px solid #f59e0b",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = "translateY(-5px)";
                    e.target.style.boxShadow =
                      "0 10px 25px rgba(245,158,11,0.25)";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      color: "#92400e",
                      marginBottom: "0.5rem",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                    }}
                  >
                    <i
                      className="fas fa-plane me-2"
                      style={{ color: "#f59e0b", fontSize: "1.2rem" }}
                    ></i>
                    Migration
                  </div>
                  <div
                    style={{
                      fontWeight: 800,
                      color: "#92400e",
                      fontSize: "2.2rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {applicationsStatistics?.totals?.total_applications -
                      (applicationsStatistics?.totals?.total_educations || 0)}
                  </div>
                  <div
                    className="badge"
                    style={{
                      backgroundColor: "#f59e0b",
                      color: "white",
                      fontSize: "0.75rem",
                      padding: "0.4rem 0.8rem",
                    }}
                  >
                    Visa Applications
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end mb-2">
              <button
                className="btn btn-primary transition duration-300 shadow-md rounded-lg px-4 py-2"
                onClick={() => setShowMore(!showMore)}
              >
                {showMore ? (
                  <span className="flex items-center gap-2">
                    Show Less <FaArrowUp />
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Show More <FaArrowDown />
                  </span>
                )}
              </button>
            </div>
            {/* === Education and Migration by Currency === */}
            <div
              className="row g-4"
              style={{
                maxHeight: showMore ? "100%" : "350px",
                overflow: showMore ? "visible" : "hidden",
              }}
            >
              {/* Education Block */}
              <div className="col-lg-6">
                <div
                  className="p-4 rounded-4 shadow-sm h-100"
                  style={{
                    background:
                      "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)",
                    border: "1px solid #bbf7d0",
                  }}
                >
                  <div
                    className="d-flex align-items-center mb-4 pb-3"
                    style={{
                      fontWeight: 700,
                      color: "#047857",
                      fontSize: "1.2rem",
                      borderBottom: "2px solid #10b981",
                    }}
                  >
                    <div
                      className="me-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "40px",
                        height: "40px",
                        background: "linear-gradient(135deg, #10b981, #059669)",
                        borderRadius: "12px",
                        color: "white",
                      }}
                    >
                      <i className="fas fa-graduation-cap"></i>
                    </div>
                    Education Applications
                  </div>

                  <div>
                    <div className="row g-3">
                      {applicationsStatistics?.educations?.map((edu, index) => (
                        <div className="col-12" key={index}>
                          <div
                            className="border-0 rounded-3 p-3 h-100 shadow-sm"
                            style={{
                              backgroundColor: "white",
                              border: "1px solid #d1fae5",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6
                                style={{
                                  color: "#047857",
                                  fontSize: "1rem",
                                  fontWeight: 700,
                                  margin: 0,
                                }}
                              >
                                {edu.currency}
                              </h6>
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: "#10b981",
                                  color: "white",
                                  fontSize: "0.8rem",
                                  padding: "0.4rem 0.8rem",
                                }}
                              >
                                {edu.total_educations} Applications
                              </span>
                            </div>

                            <div
                              className="mb-3 p-2 rounded-2"
                              style={{ backgroundColor: "#f0fdf4" }}
                            >
                              <div
                                style={{
                                  color: "#374151",
                                  fontSize: "0.85rem",
                                }}
                              >
                                <strong>Total Cost: </strong>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    color: "#047857",
                                    fontSize: "1rem",
                                  }}
                                >
                                  {edu.total_charges} {edu.currency}
                                </span>
                              </div>
                            </div>

                            <div className="row g-2">
                              <div className="col-4">
                                <div
                                  className="text-center p-2 rounded-2"
                                  style={{ backgroundColor: "#dcfce7" }}
                                >
                                  <div
                                    className="badge bg-success mb-1"
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    Paid
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.9rem",
                                      fontWeight: 600,
                                      color: "#047857",
                                    }}
                                  >
                                    {edu.paid_count}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#059669",
                                    }}
                                  >
                                    {edu.total_paid} {edu.currency}
                                  </div>
                                </div>
                              </div>
                              <div className="col-4">
                                <div
                                  className="text-center p-2 rounded-2"
                                  style={{ backgroundColor: "#fef3c7" }}
                                >
                                  <div
                                    className="badge bg-warning mb-1"
                                    style={{ fontSize: "0.7rem" }}
                                  >
                                    Pending
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.9rem",
                                      fontWeight: 600,
                                      color: "#92400e",
                                    }}
                                  >
                                    {edu.pending_count}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#d97706",
                                    }}
                                  >
                                    {edu.total_remaining} {edu.currency}
                                  </div>
                                </div>
                              </div>
                              {/* <div className="col-4">
                              <div
                                className="text-center p-2 rounded-2"
                                style={{ backgroundColor: "#fee2e2" }}
                              >
                                <div
                                  style={{
                                    fontSize: "0.9rem",
                                    fontWeight: 600,
                                    color: "#dc2626",
                                  }}
                                >
                                  {edu.overpaid_count}
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.75rem",
                                    color: "#ef4444",
                                  }}
                                >
                                  {edu.total_overpaid} {edu.currency}
                                </div>
                              </div>
                            </div> */}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Migration Block */}
              <div className="col-lg-6">
                <div
                  className="p-4 rounded-4 shadow-sm h-100"
                  style={{
                    background:
                      "linear-gradient(135deg, #fef3c7 0%, #fef7cd 100%)",
                    border: "1px solid #fde68a",
                  }}
                >
                  <div
                    className="d-flex align-items-center mb-4 pb-3"
                    style={{
                      fontWeight: 700,
                      color: "#92400e",
                      fontSize: "1.2rem",
                      borderBottom: "2px solid #f59e0b",
                    }}
                  >
                    <div
                      className="me-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "40px",
                        height: "40px",
                        background: "linear-gradient(135deg, #f59e0b, #d97706)",
                        borderRadius: "12px",
                        color: "white",
                      }}
                    >
                      <i className="fas fa-plane"></i>
                    </div>
                    Migration Applications
                  </div>

                  <div className="row g-3">
                    {applicationsStatistics?.migrations?.map((mig, index) => (
                      <div className="col-12" key={index}>
                        <div
                          className="border-0 rounded-3 p-3 h-100 shadow-sm"
                          style={{
                            backgroundColor: "white",
                            border: "1px solid #fde68a",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6
                              style={{
                                color: "#92400e",
                                fontSize: "1rem",
                                fontWeight: 700,
                                margin: 0,
                              }}
                            >
                              {mig.currency}
                            </h6>
                            <span
                              className="badge"
                              style={{
                                backgroundColor: "#f59e0b",
                                color: "white",
                                fontSize: "0.8rem",
                                padding: "0.4rem 0.8rem",
                              }}
                            >
                              {mig.total_applications} Applications
                            </span>
                          </div>

                          <div
                            className="mb-3 p-2 rounded-2"
                            style={{ backgroundColor: "#fffbeb" }}
                          >
                            <div
                              style={{ color: "#374151", fontSize: "0.85rem" }}
                            >
                              <strong>Total Cost: </strong>
                              <span
                                style={{
                                  fontWeight: 700,
                                  color: "#92400e",
                                  fontSize: "1rem",
                                }}
                              >
                                {mig.total_charges} {mig.currency}
                              </span>
                            </div>
                          </div>

                          <div className="row g-2">
                            <div className="col-4">
                              <div
                                className="text-center p-2 rounded-2"
                                style={{ backgroundColor: "#dcfce7" }}
                              >
                                <div
                                  className="badge bg-success mb-1"
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  Paid
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.9rem",
                                    fontWeight: 600,
                                    color: "#047857",
                                  }}
                                >
                                  {mig.paid_count}
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.75rem",
                                    color: "#059669",
                                  }}
                                >
                                  {mig.total_paid} {mig.currency}
                                </div>
                              </div>
                            </div>
                            <div className="col-4">
                              <div
                                className="text-center p-2 rounded-2"
                                style={{ backgroundColor: "#fef3c7" }}
                              >
                                <div
                                  className="badge bg-warning mb-1"
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  Pending
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.9rem",
                                    fontWeight: 600,
                                    color: "#92400e",
                                  }}
                                >
                                  {mig.pending_count}
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.75rem",
                                    color: "#d97706",
                                  }}
                                >
                                  {mig.total_remaining} {mig.currency}
                                </div>
                              </div>
                            </div>
                            {/* <div className="col-4">
                              <div
                                className="text-center p-2 rounded-2"
                                style={{ backgroundColor: "#fee2e2" }}
                              >
                                <div
                                  className="badge bg-danger mb-1"
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  Overpaid
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.9rem",
                                    fontWeight: 600,
                                    color: "#dc2626",
                                  }}
                                >
                                  {mig.overpaid_count}
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.75rem",
                                    color: "#ef4444",
                                  }}
                                >
                                  {mig.total_overpaid} {mig.currency}
                                </div>
                              </div>
                            </div> */}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="card mb-4"
        style={{
          border: "none",
          borderRadius: "1rem",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div className="card-body" style={{ padding: "1rem" }}>
          <h5
            style={{
              fontWeight: "600",
              color: "#1f2937",
              marginBottom: "1.5rem",
              fontSize: "1.125rem",
            }}
          >
            Application Filters
          </h5>

          <div className="row g-3">
            <div className="d-flex gap-0.5">
              {/* Search */}
              <div className="col-md-3">
                <label
                  className="form-label"
                  style={{
                    fontWeight: 500,
                    color: "#374151",
                    fontSize: "0.875rem",
                  }}
                >
                  Search Client Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by client name..."
                  value={filters.search}
                  onChange={(e) =>
                    handleFilterChange("searchByName", e.target.value)
                  }
                />
              </div>

              {/* Type */}
              <div className="col-md-3">
                <label
                  className="form-label"
                  style={{
                    fontWeight: 500,
                    color: "#374151",
                    fontSize: "0.875rem",
                  }}
                >
                  Type
                </label>
                <select
                  className="form-select"
                  value={filters.type}
                  onChange={(e) => handleFilterChange("type", e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="education">Education</option>
                  <option value="migration">Migration</option>
                </select>
              </div>

              {/* Currency */}
              <div className="col-md-3">
                <label
                  className="form-label"
                  style={{
                    fontWeight: 500,
                    color: "#374151",
                    fontSize: "0.875rem",
                  }}
                >
                  Currency
                </label>
                <select
                  className="form-select"
                  value={filters.currency}
                  onChange={(e) =>
                    handleFilterChange("currency", e.target.value)
                  }
                >
                  <option value="">All Currencies</option>
                  <option value="AUD">AUD</option>
                  <option value="AED">AED</option>
                  <option value="CAD">CAD</option>
                  <option value="GBP">GBP</option>
                  <option value="PKR">PKR</option>
                  <option value="USD">USD</option>
                </select>
              </div>

              {/* Status */}
              <div className="col-md-3">
                <label
                  className="form-label"
                  style={{
                    fontWeight: 500,
                    color: "#374151",
                    fontSize: "0.875rem",
                  }}
                >
                  Status
                </label>
                <select
                  className="form-select"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="Paid">Paid</option>
                  <option value="Pending">Pending Payment</option>
                  <option value="Unknown">Unknown</option>
                </select>
              </div>
            </div>

            <div className="d-flex gap-0.5">
              {/* Amount Range */}
              <div className="col-md-6">
                <label
                  className="form-label"
                  style={{
                    fontWeight: 500,
                    color: "#374151",
                    fontSize: "0.875rem",
                  }}
                >
                  Professional Amount Range
                </label>
                <div className="d-flex gap-2">
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Min"
                    value={filters.amountMin}
                    onChange={(e) =>
                      handleFilterChange("amountMin", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Max"
                    value={filters.amountMax}
                    onChange={(e) =>
                      handleFilterChange("amountMax", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="col-md-6">
                <label
                  className="form-label"
                  style={{
                    fontWeight: 500,
                    color: "#374151",
                    fontSize: "0.875rem",
                  }}
                >
                  Application's Date Range
                </label>
                <div className="d-flex gap-2">
                  <input
                    type="date"
                    className="form-control"
                    value={filters.dateFrom}
                    onChange={(e) =>
                      handleFilterChange("dateFrom", e.target.value)
                    }
                  />
                  <input
                    type="date"
                    className="form-control"
                    value={filters.dateTo}
                    onChange={(e) =>
                      handleFilterChange("dateTo", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
            {/* Actions */}
            <div className="col-md-12 d-flex justify-content-end gap-2 mt-3">
              <button
                onClick={clearFilters}
                className="btn btn-light"
                style={{ border: "1px solid #d1d5db", fontWeight: 500 }}
              >
                Clear All
              </button>
              <button
                onClick={applyFilters}
                className="btn btn-primary"
                style={{ fontWeight: 500 }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          {selectedApplications.size > 0 && (
            <span
              className="badge"
              style={{
                backgroundColor: "#dbeafe",
                color: "#1e40af",
                fontSize: "0.75rem",
                padding: "0.375rem 0.75rem",
                fontWeight: "500",
              }}
            >
              {selectedApplications.size} selected
            </span>
          )}
        </div>
      </div>

      <div className="table-responsive mb-2 rounded-md">
        <table className="table table-bordered bg-white mb-0">
          <thead className="table-dark">
            <tr>
              {Object.entries(tableHeaders).map(([key, label]) => (
                <th
                  key={key}
                  style={{
                    padding: "0.25rem",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    border: "none",
                    verticalAlign: "middle",
                    cursor: "pointer",
                  }}
                >
                  {label}
                  {sortConfig.key === key && (
                    <span className="ms-2">
                      {sortConfig.direction === "asc" ? (
                        <FaArrowUp size={12} />
                      ) : (
                        <FaArrowDown size={12} />
                      )}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={Object.keys(tableHeaders).length + 1}
                  className="text-center py-5"
                >
                  <Loader />
                </td>
              </tr>
            ) : sortedRecords.length === 0 ? (
              <tr>
                <td
                  colSpan={Object.keys(tableHeaders).length + 1}
                  className="text-center py-5"
                >
                  <div
                    style={{
                      color: "#64748b",
                      fontSize: "1rem",
                      fontWeight: "500",
                    }}
                  >
                    No applications found
                  </div>
                  <div
                    style={{
                      color: "#94a3b8",
                      fontSize: "0.875rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    Adjust filters to see results
                  </div>
                </td>
              </tr>
            ) : (
              sortedRecords.map((app) => (
                <tr key={app.id}>
                  {/* 2. Client Name */}
                  <td>
                    <Link
                      to={`/clients/view-client-detail/${app?.client?.id}`}
                      className="text-decoration-none"
                    >
                      {app?.client_name || "—"}
                    </Link>
                  </td>

                  {/* 3. Application Name */}
                  <td
                    style={{
                      padding: "0.25rem",
                      verticalAlign: "middle",
                    }}
                  >
                    {app.application_name}
                  </td>

                  {/* 4. Type */}
                  <td
                    style={{
                      padding: "0.25rem",
                      verticalAlign: "middle",
                    }}
                  >
                    <span
                      style={{
                        backgroundColor:
                          app.type === "education" ? "#eff6ff" : "#fffbeb",
                        color: app.type === "education" ? "#1d4ed8" : "#b45309",
                        fontWeight: "500",
                        fontSize: "0.75rem",
                        padding: "0.375rem 0.75rem",
                        borderRadius: "9999px",
                        border: `1px solid ${
                          app.type === "education" ? "#bfdbfe" : "#fcd34d"
                        }`,
                      }}
                    >
                      {app.type === "education" ? "Education" : "Migration"}
                    </span>
                  </td>

                  {/* 6. Professional Cost (total_charges) */}
                  <td
                    style={{
                      padding: "0.25rem",
                      fontWeight: "500",
                      verticalAlign: "middle",
                      textAlign: "center",
                    }}
                  >
                    {app.currency}{" "}
                    {app.financial_summary?.total_charges?.toLocaleString() ||
                      0}
                  </td>

                  {/* 7. Paid (total_payments) */}
                  <td
                    style={{
                      padding: "0.25rem",
                      fontWeight: "500",
                      verticalAlign: "middle",
                      textAlign: "center",
                    }}
                  >
                    {app.currency}{" "}
                    {app.financial_summary?.total_payments?.toLocaleString() ||
                      0}
                  </td>

                  {/* 8. Discount (total_discounts) */}
                  <td
                    style={{
                      padding: "0.25rem",
                      fontWeight: "500",
                      verticalAlign: "middle",
                      textAlign: "center",
                    }}
                  >
                    {app.currency}{" "}
                    {app.financial_summary?.total_discounts?.toLocaleString() ||
                      0}
                  </td>

                  {/* 9. Remaining Balance */}
                  <td
                    style={{
                      padding: "0.25rem",
                      fontWeight: "500",
                      verticalAlign: "middle",
                      textAlign: "center",
                    }}
                  >
                    {app.currency}{" "}
                    {Math.abs(
                      app.financial_summary?.remaining_balance || 0,
                    ).toLocaleString()}
                    {app.financial_summary?.remaining_balance < 0 && " (Over)"}
                  </td>

                  {/* 10. Over Paid */}
                  {/* <td
                      style={{
                        padding: "0.25rem",
                        fontWeight: "500",
                        verticalAlign: "middle",
                        textAlign: "center",
                      }}
                    >
                      {app.currency}{" "}
                      {app.financial_summary?.overpayment_credit?.toLocaleString() ||
                        0}
                    </td> */}

                  <td
                    style={{
                      padding: "0.25rem",
                      fontWeight: "500",
                      verticalAlign: "middle",
                    }}
                  >
                    {app?.financial_summary?.status && (
                      <span
                        style={{
                          padding: "0.5rem 0.75rem",
                          borderRadius: "9999px", // pill shape
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textTransform: "capitalize",
                          backgroundColor:
                            app.financial_summary.status === "Paid"
                              ? "#d1fae5" // light green
                              : app.financial_summary.status === "Pending"
                                ? "#fef3c7" // light yellow
                                : app.financial_summary.status === "Overpaid"
                                  ? "#fee2e2" // light red
                                  : "#f3f4f6", // gray fallback
                          color:
                            app.financial_summary.status === "Paid"
                              ? "#065f46" // dark green
                              : app.financial_summary.status === "Pending"
                                ? "#92400e" // dark yellow
                                : app.financial_summary.status === "Overpaid"
                                  ? "#991b1b" // dark red
                                  : "#374151", // dark gray
                        }}
                      >
                        {app.financial_summary.status}
                      </span>
                    )}
                  </td>

                  {/* 11. Plan Frequency */}
                  {/* <td
                      style={{
                        padding: "0.25rem",
                        verticalAlign: "middle",
                      }}
                    >
                      {app.has_plan ? app.plan_frequency : "One-time"}
                    </td> */}

                  {/* 12. Installments Count */}
                  {/* <td
                      style={{
                        padding: "0.25rem",
                        fontWeight: "500",
                        verticalAlign: "middle",
                        textAlign: "center",
                      }}
                    >
                      {app.has_plan ? app.plan_installments_count : 1}
                    </td> */}

                  {/* 13. Created At */}
                  <td
                    style={{
                      padding: "0.25rem",
                      fontSize: "0.8125rem",
                      color: "#64748b",
                      verticalAlign: "middle",
                    }}
                  >
                    {new Date(app.created_at).toLocaleDateString()}
                  </td>
                  <td
                    style={{
                      padding: "0.25rem",
                      fontSize: "0.8125rem",
                      color: "#64748b",
                      verticalAlign: "middle",
                    }}
                  >
                    <button
                      className="btn btn-primary size-12 m-1"
                      onClick={() => {
                        handleOpenDiscountModal(app);
                      }}
                    >
                      <FaDollarSign size={10} color={"#ffffffed"} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination Section */}
      <div className="row align-items-center mb-5 mt-2">
        <div className="col-auto">
          <select
            value={pagination?.perPage}
            onChange={handlePerPageChange}
            className="form-select"
            style={{ minWidth: 80 }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
          </select>
        </div>
        <div className="col-auto text-muted">
          Page {pagination?.currentPage} Showing{" "}
          {pagination?.perPage >= pagination?.total
            ? pagination?.total
            : pagination?.perPage}{" "}
          of {pagination?.total} entries
        </div>
        <div className="col">
          <ReactPaginate
            previousLabel={"Previous"}
            nextLabel={"Next"}
            breakLabel={"..."}
            pageCount={pagination.lastPage}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageChange}
            forcePage={pagination.currentPage - 1}
            containerClassName={"pagination justify-content-end mb-0"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            activeClassName={"active"}
            disabledClassName={"disabled"}
          />
        </div>
      </div>
      {/* Discount Modal */}
      {openDiscountModal && (
        <DiscountModal
          show={true}
          onClose={() => {
            setOpenDiscountModal(false);
            fetchApplicationWithInvoices();
          }}
          selectedApplication={selectedApplication}
          type={type}
          clientData={clientData}
        />
      )}
    </div>
  );
};

export default ProfessionalCost;
